
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { PurchaseOrderApi } from '../types';
import DateRangePicker from '../components/DateRangePicker';
import DatePicker from '../components/DatePicker';
import SearchHistoryInput from '../components/SearchHistoryInput';
import PeopleHoverCard from '../components/PeopleHoverCard';
import { getAssignedToPeople, getAssignedByPeople } from '../utils/poPeople';
import { parseSizeWiseSheet, SizeWiseResult } from '../utils/poSizeWiseImport';

const API_BASE = import.meta.env.VITE_API_BASE_URL;
const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
});

const COLUMN_DEFS: { key: string; label: string }[] = [
  { key: 'po_number', label: 'PO Number' },
  { key: 'work_order', label: 'Work Order' },
  { key: 'customer', label: 'Customer' },
  { key: 'assigned_to', label: 'Assigned To' },
  { key: 'assigned_by', label: 'Assigned By' },
  { key: 'ship_date', label: 'Ship Date' },
  { key: 'inspections', label: 'Inspections' },
  { key: 'actions', label: 'Actions' },
];

const COLUMN_VISIBILITY_STORAGE_KEY = 'po-list-visible-columns';

const loadVisibleColumns = (): Record<string, boolean> => {
  const defaults: Record<string, boolean> = {};
  COLUMN_DEFS.forEach(c => { defaults[c.key] = true; });
  try {
    const saved = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      COLUMN_DEFS.forEach(c => {
        if (typeof parsed[c.key] === 'boolean') defaults[c.key] = parsed[c.key];
      });
    }
  } catch {
    // ignore malformed storage value, fall back to defaults
  }
  return defaults;
};

const PurchaseOrders: React.FC = () => {
  const navigate = useNavigate();
  const [isNewPOModalOpen, setIsNewPOModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isSyncSuccessModalOpen, setIsSyncSuccessModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUploadSuccessModalOpen, setIsUploadSuccessModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [selectedPOs, setSelectedPOs] = useState<string[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrderApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [users, setUsers] = useState<{ id: string; name: string; profileImage: string | null }[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<PurchaseOrderApi | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{ poCount: number; skuCount: number } | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(loadVisibleColumns);
  const [isColumnsMenuOpen, setIsColumnsMenuOpen] = useState(false);
  const columnsMenuRef = useRef<HTMLDivElement>(null);

  const visibleColumnCount = COLUMN_DEFS.filter(c => visibleColumns[c.key]).length;

  const toggleColumn = (key: string) => {
    setVisibleColumns(prev => {
      const next = { ...prev, [key]: !prev[key] };
      // Keep at least one column visible
      if (Object.values(next).every(v => !v)) return prev;
      return next;
    });
  };

  useEffect(() => {
    try {
      localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(visibleColumns));
    } catch {
      // ignore storage write failures (e.g. private browsing)
    }
  }, [visibleColumns]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (columnsMenuRef.current && !columnsMenuRef.current.contains(e.target as Node)) {
        setIsColumnsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      params.set('page', String(currentPage));
      params.set('limit', '20');

      const response = await fetch(`${API_BASE}/api/v1/purchase-orders?${params}`, { headers: getHeaders() });
      const data = await response.json();
      if (data.success) {
        setPurchaseOrders(data.data);
        setTotalPages(data.totalPages);
        setTotalCount(data.count);
      }
    } catch (err) {
      console.error('Failed to fetch purchase orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/users`, { headers: getHeaders() });
      const data = await response.json();
      if (data.success) {
        setUsers(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  useEffect(() => {
    fetchPurchaseOrders();
  }, [currentPage]);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchPurchaseOrders();
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, dateFrom, dateTo]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleSyncFromERP = () => {
    setIsNewPOModalOpen(false);
    setIsSyncSuccessModalOpen(true);
  };

  const handleOpenUpload = () => {
    setIsNewPOModalOpen(false);
    setIsUploadModalOpen(true);
  };

  const handleExportExcel = () => {
    if (purchaseOrders.length === 0) return;

    const colWidths: Record<string, number> = {
      'PO Number': 16, 'Work Order': 16, 'Customer': 24, 'Location': 20,
      'Inspector': 22, 'Assigned By': 22, 'Ship Date': 14,
      'Pilot': 12, 'Fabric': 12, 'Line': 12, 'Final': 12, 'Container': 12,
    };

    const rows = purchaseOrders.map((po) => {
      const statuses = po.inspection_statuses || {};
      const row: Record<string, string> = {};
      if (visibleColumns.po_number) row['PO Number'] = po.po_number;
      if (visibleColumns.work_order) row['Work Order'] = po.work_order || '';
      if (visibleColumns.customer) {
        row['Customer'] = po.customer_name;
        row['Location'] = po.customer_location || '';
      }
      if (visibleColumns.assigned_to) {
        row['Inspector'] = getAssignedToPeople(po)
          .map(p => (p.status === 'completed' ? `${p.name} (Completed)` : p.name))
          .join(', ') || 'Unassigned';
      }
      if (visibleColumns.assigned_by) row['Assigned By'] = getAssignedByPeople(po).map(p => p.name).join(', ') || '-';
      if (visibleColumns.ship_date) row['Ship Date'] = formatDate(po.ship_date);
      if (visibleColumns.inspections) {
        row['Pilot'] = statuses.pilot || 'Pending';
        row['Fabric'] = statuses.fabric || 'Pending';
        row['Line'] = statuses.line || 'Pending';
        row['Final'] = statuses.final || 'Pending';
        row['Container'] = statuses.container || 'Pending';
      }
      return row;
    });

    if (rows.length === 0 || Object.keys(rows[0]).length === 0) return;

    const ws = XLSX.utils.json_to_sheet(rows);

    // Style column widths for readability, matching only the exported columns
    ws['!cols'] = Object.keys(rows[0]).map(key => ({ wch: colWidths[key] || 14 }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Purchase Orders');

    const today = new Date();
    const fileName = `Purchase Orders ${today.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const handleManualEntry = () => {
    setIsNewPOModalOpen(false);
    navigate('/purchase-orders/add');
  };

  const handleDeletePO = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      const response = await fetch(`${API_BASE}/api/v1/purchase-orders/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setDeleteTarget(null);
        fetchPurchaseOrders();
      }
    } catch (err) {
      console.error('Failed to delete purchase order:', err);
    } finally {
      setDeleting(false);
    }
  };

  const downloadSampleCSV = () => {
    const csv = `po_number,work_order,customer_name,ship_date,sku_number,product_category,vendor_code,description,color,size,total_qty,qty_per_pack,unit_price
PO-2026-001,WO-2026-001,Zara International,2026-05-15,SKU-1001,Denim,VND-101,Slim Fit Jeans,Indigo Blue,32,1200,24,12.50
PO-2026-001,WO-2026-001,Zara International,2026-05-15,SKU-1002,Denim,VND-101,Straight Fit Jeans,Black,34,960,24,13.00
PO-2026-001,WO-2026-001,Zara International,2026-05-15,SKU-1003,Knits,VND-102,Cotton Polo Shirt,White,M,1800,30,8.75
PO-2026-002,WO-2026-002,H&M Group,2026-06-01,SKU-2001,Woven,VND-201,Oxford Button Down,Sky Blue,L,1600,20,10.00
PO-2026-002,WO-2026-002,H&M Group,2026-06-01,SKU-2002,Woven,VND-201,Linen Casual Shirt,Beige,XL,900,20,11.50
PO-2026-003,WO-2026-003,Primark Ltd,2026-05-20,SKU-3001,Knits,VND-301,Basic Crew Neck Tee,Navy,S,5000,50,4.25`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'purchase_order_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Size-wise template: title row, header row, one column per size (blank = size not ordered).
  const downloadSizeWiseSample = () => {
    const sizes = ['8', '10', '12', '14', '16', '18', 'S', 'M', 'L', 'XL', 'XXL'];
    const header = ['BUYER', 'PO NO.', 'Work Order Number', 'SKU Number', 'SKU DESCRIPTION', 'Product Category', 'Ship date', 'COLOUR', ...sizes, 'TOTAL QTY', 'Qty Per pack'];
    const firstSizeCol = header.indexOf(sizes[0]);
    const shipDateCol = header.indexOf('Ship date');
    const excelDate = (iso: string) => Date.parse(iso) / 86400000 + 25569;
    const line = (po: string, wo: string, sku: string, desc: string, category: string, shipDate: string, colour: string, qtys: Record<string, number>) => [
      'ABC Outdoor', po, wo, sku, desc, category, excelDate(shipDate), colour,
      ...sizes.map(s => qtys[s] ?? ''),
      Object.values(qtys).reduce((sum, q) => sum + q, 0), 1,
    ];
    const ws = XLSX.utils.aoa_to_sheet([
      header.map((_, i) => (i === 0 ? 'SIZE-WISE PO FORMAT' : i === firstSizeCol ? 'Size' : '')),
      header,
      line('PO-2026-101', 'WO-101 A', 'SKU-5001', 'Girls Tank Top', 'Kids Tee', '2026-11-30', 'Navy', { 8: 20, 10: 40, 12: 50, 14: 45, 16: 30, 18: 15 }),
      line('PO-2026-101', 'WO-101 B', 'SKU-5001', 'Girls Tank Top', 'Kids Tee', '2026-11-30', 'Coral', { 8: 15, 10: 35, 12: 40, 14: 35, 16: 25 }),
      line('PO-2026-101', 'WO-101 C', 'SKU-5002', 'Mens Crew Tee', 'Mens Tee', '2026-11-30', 'Graphite', { S: 30, M: 60, L: 60, XL: 35, XXL: 15 }),
      line('PO-2026-102', 'WO-102 A', 'SKU-5003', 'Mens Polo', 'Mens Tee', '2026-12-15', 'Sage', { S: 40, M: 80, L: 90, XL: 60, XXL: 30 }),
    ]);
    for (let r = 2; r < 6; r++) ws[XLSX.utils.encode_cell({ r, c: shipDateCol })].z = 'dd-mm-yyyy';
    ws['!merges'] = [{ s: { r: 0, c: firstSizeCol }, e: { r: 0, c: firstSizeCol + sizes.length - 1 } }];
    ws['!cols'] = header.map((h, i) => ({ wch: i >= firstSizeCol && i < firstSizeCol + sizes.length ? 5 : Math.max(12, h.length + 2) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, 'purchase_order_size_wise_sample.xlsx');
  };

  // The size-wise format is told apart by its headers; null means "use the standard import".
  // Read without cellDates so ship dates keep their Excel serial (no timezone shift).
  const readSizeWiseFile = async (file: File, extension: string | undefined): Promise<SizeWiseResult | null> => {
    let workbook: XLSX.WorkBook;
    try {
      if (extension === 'csv') workbook = XLSX.read(await file.text(), { type: 'string', raw: true });
      else if (extension === 'xlsx' || extension === 'xls') workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      else return null;
    } catch {
      return null; // leave unreadable files to the standard import's own handling
    }
    return parseSizeWiseSheet(workbook.Sheets[workbook.SheetNames[0]], normalizeDate);
  };

  // Normalize date strings written by Excel after Save (e.g. "15-05-2026", "5/15/2026")
  // back to ISO YYYY-MM-DD that the API expects. Returns null for empty/unparseable input.
  const normalizeDate = (raw: any): string | null => {
    if (raw === null || raw === undefined || raw === '') return null;
    if (raw instanceof Date && !isNaN(raw.getTime())) {
      const y = raw.getFullYear();
      const m = String(raw.getMonth() + 1).padStart(2, '0');
      const d = String(raw.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    const s = String(raw).trim();
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const parts = s.split(/[\/\-.]/).map(p => p.trim());
    if (parts.length === 3) {
      let [a, b, c] = parts;
      // YYYY-M-D or YYYY/M/D
      if (a.length === 4) {
        return `${a}-${b.padStart(2, '0')}-${c.padStart(2, '0')}`;
      }
      // DD-MM-YYYY / DD/MM/YYYY (assume day-first; if first part > 12 it's definitely day)
      if (c.length === 4) {
        const first = parseInt(a, 10);
        const second = parseInt(b, 10);
        let day = a, month = b;
        if (first > 12 && second <= 12) { day = a; month = b; }
        else if (second > 12 && first <= 12) { day = b; month = a; }
        return `${c}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    const parsed = new Date(s);
    if (!isNaN(parsed.getTime())) {
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, '0');
      const d = String(parsed.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    return null;
  };

  const parseCSV = (text: string): Record<string, string>[] => {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim());
    const rows: Record<string, string>[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values: string[] = [];
      let current = '';
      let inQuotes = false;
      for (const char of lines[i]) {
        if (char === '"') { inQuotes = !inQuotes; }
        else if (char === ',' && !inQuotes) { values.push(current.trim()); current = ''; }
        else { current += char; }
      }
      values.push(current.trim());
      const row: Record<string, string> = {};
      headers.forEach((h, idx) => { row[h] = values[idx] || ''; });
      rows.push(row);
    }
    return rows;
  };

  // POST each PO in turn; a rejected PO (e.g. duplicate PO number) doesn't stop the rest.
  const createUploadedPOs = async (bodies: { po_number: string; skus: unknown[] }[]) => {
    let createdCount = 0;
    let totalSkus = 0;
    const errors: string[] = [];

    for (const body of bodies) {
      const response = await fetch(`${API_BASE}/api/v1/purchase-orders`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (data.success) {
        createdCount++;
        totalSkus += body.skus.length;
      } else {
        errors.push(`${body.po_number}: ${data.message}`);
      }
    }

    if (errors.length > 0) {
      setUploadError(`Created ${createdCount} POs. Errors: ${errors.join('; ')}`);
    } else {
      setUploadResult({ poCount: createdCount, skuCount: totalSkus });
      setIsUploadModalOpen(false);
      setIsUploadSuccessModalOpen(true);
    }
    setUploadFile(null);
    fetchPurchaseOrders();
  };

  const handleUploadFile = async () => {
    if (!uploadFile) { setUploadError('Please select a file.'); return; }
    setUploadError('');
    setUploading(true);

    try {
      let rows: Record<string, any>[] = [];
      const extension = uploadFile.name.split('.').pop()?.toLowerCase();

      const sizeWise = await readSizeWiseFile(uploadFile, extension);
      if (sizeWise) {
        if ('error' in sizeWise) { setUploadError(sizeWise.error); return; }
        // Columns this format doesn't have stay blank; work order lives on each SKU line.
        await createUploadedPOs(sizeWise.pos.map(po => ({
          po_number: po.po_number,
          customer_name: po.customer_name,
          customer_location: '',
          work_order: null,
          ship_date: po.ship_date,
          source: 'upload',
          skus: po.skus.map(sku => ({
            ...sku,
            work_order: sku.work_order || null,
            vendor_code: '',
            unit_price: null,
          })),
        })));
        return;
      }

      if (extension === 'csv') {
        const text = await uploadFile.text();
        rows = parseCSV(text);
      } else if (extension === 'xlsx' || extension === 'xls') {
        const data = await uploadFile.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        rows = XLSX.utils.sheet_to_json(worksheet, { defval: '', raw: false, dateNF: 'yyyy-mm-dd' });
      } else {
        setUploadError('Unsupported file format. Please use CSV or Excel.');
        setUploading(false);
        return;
      }

      // Filter out completely empty rows (e.g. from trailing commas or blank rows in Excel)
      rows = rows.filter(row => Object.values(row).some(val => val !== null && val !== undefined && String(val).trim() !== ''));

      if (rows.length === 0) { setUploadError('File is empty or has no data rows.'); setUploading(false); return; }

      // Validate required columns
      const requiredCols = ['po_number', 'customer_name', 'sku_number'];
      const missingCols = requiredCols.filter(c => !(c in rows[0]));
      if (missingCols.length > 0) { setUploadError(`Missing required columns: ${missingCols.join(', ')}`); setUploading(false); return; }

      // Validate rows have sku_number
      for (let i = 0; i < rows.length; i++) {
        if (!rows[i].sku_number?.trim()) { setUploadError(`Row ${i + 2}: SKU Number is required.`); setUploading(false); return; }
        if (!rows[i].po_number?.trim()) { setUploadError(`Row ${i + 2}: PO Number is required.`); setUploading(false); return; }
      }

      // Group by po_number
      const poMap = new Map<string, Record<string, string>[]>();
      rows.forEach(row => {
        const key = row.po_number.trim();
        if (!poMap.has(key)) poMap.set(key, []);
        poMap.get(key)!.push(row);
      });

      // Validate conflicts — same po_number must have same PO-level fields
      const poFields = ['customer_name', 'work_order', 'ship_date'];
      for (const [poNum, poRows] of poMap) {
        const first = poRows[0];
        for (let i = 1; i < poRows.length; i++) {
          for (const field of poFields) {
            const a = field === 'ship_date'
              ? (normalizeDate(poRows[i][field]) || '')
              : String(poRows[i][field] || '').trim();
            const b = field === 'ship_date'
              ? (normalizeDate(first[field]) || '')
              : String(first[field] || '').trim();
            if (a !== b) {
              setUploadError(`PO "${poNum}" has conflicting "${field}": "${first[field]}" (row ${rows.indexOf(poRows[0]) + 2}) vs "${poRows[i][field]}" (row ${rows.indexOf(poRows[i]) + 2}). All rows for the same PO must have identical PO-level details.`);
              setUploading(false);
              return;
            }
          }
        }
      }

      // Create POs via API
      await createUploadedPOs([...poMap.values()].map(poRows => {
        const first = poRows[0];
        return {
          po_number: first.po_number.trim(),
          customer_name: first.customer_name.trim(),
          customer_location: first.customer_location?.trim() || '',
          work_order: first.work_order?.trim() || null,
          ship_date: normalizeDate(first.ship_date),
          source: 'upload',
          skus: poRows.map(r => ({
            sku_number: r.sku_number.trim(),
            product_category: r.product_category?.trim() || '',
            vendor_code: r.vendor_code?.trim() || '',
            description: r.description?.trim() || '',
            color: r.color?.trim() || '',
            size: r.size?.trim() || '',
            total_qty: parseInt(r.total_qty) || 0,
            qty_per_pack: parseInt(r.qty_per_pack) || 0,
            unit_price: parseFloat(r.unit_price) || null,
          })),
        };
      }));
    } catch (err) {
      setUploadError('Failed to process file. Please check the format.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mx-auto w-full relative">
      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/50 backdrop-blur-[4px] px-4 sm:px-0">
          <div
            className="absolute inset-0 bg-slate-900/60 transition-opacity"
            onClick={() => setDeleteTarget(null)}
          ></div>
          <div className="relative w-full max-w-sm transform overflow-hidden rounded-2xl bg-white p-8 shadow-2xl transition-all border border-slate-100 text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 ring-1 ring-red-100">
              <span className="material-symbols-outlined text-[32px] text-red-500" style={{ fontVariationSettings: "'FILL' 1" }}>
                delete_forever
              </span>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Delete Purchase Order</h3>
            <p className="text-sm text-slate-500">
              Are you sure you want to delete <span className="font-semibold text-slate-700">{deleteTarget.po_number}</span>? This action cannot be undone.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                className="px-5 py-2.5 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition-colors"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="px-5 py-2.5 rounded-lg bg-red-600 text-sm font-medium text-white shadow-lg shadow-red-500/20 hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                onClick={handleDeletePO}
                disabled={deleting}
              >
                {deleting && <span className="material-symbols-outlined animate-spin text-[16px]">progress_activity</span>}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Success Modal Overlay */}
      {isUploadSuccessModalOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 backdrop-blur-[4px] px-4 sm:px-0">
          <div
            className="absolute inset-0 bg-slate-900/60 transition-opacity"
            onClick={() => setIsUploadSuccessModalOpen(false)}
          ></div>
          <div className="relative w-full max-w-sm transform overflow-hidden rounded-2xl bg-white p-8 shadow-2xl transition-all text-center border border-slate-100">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-50 text-[#10b981] shadow-[0_0_30px_rgba(16,185,129,0.3)] ring-1 ring-green-100">
              <span className="material-symbols-outlined text-[40px] drop-shadow-[0_0_8px_rgba(16,185,129,0.5)]" style={{ fontVariationSettings: "'FILL' 1" }}>
                check_circle
              </span>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Upload Successful!</h3>
            <p className="text-slate-500 text-sm font-medium">Purchase Order file uploaded successfully.</p>

            <div className="mt-4 flex items-center justify-center gap-3">
              <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-sm font-semibold text-[#136bce] border border-blue-100">
                <span className="material-symbols-outlined text-[18px]">inventory_2</span>
                <span>{uploadResult?.poCount || 0} POs created</span>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 border border-emerald-100">
                <span className="material-symbols-outlined text-[18px]">dataset</span>
                <span>{uploadResult?.skuCount || 0} SKUs processed</span>
              </div>
            </div>

            <div className="mt-6 h-1 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full w-full bg-[#10b981]"></div>
            </div>

            <button
              className="mt-6 text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors"
              onClick={() => { setIsUploadSuccessModalOpen(false); setUploadResult(null); }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* ERP Sync Success Modal Overlay */}
      {isSyncSuccessModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4 sm:px-0">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setIsSyncSuccessModalOpen(false)}
          ></div>
          <div className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-8 text-center shadow-2xl transition-all border border-slate-100">
            <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-emerald-400 to-[#10b981]"></div>
            <div className="flex flex-col items-center">
              <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-100" style={{ boxShadow: '0 0 25px rgba(16, 185, 129, 0.5)' }}>
                <span className="material-symbols-outlined text-5xl text-[#10b981]" style={{ fontVariationSettings: "'FILL' 1, 'wght' 700" }}>
                  check_circle
                </span>
              </div>
              <h3 className="mb-2 text-xl font-bold text-slate-900">ERP Sync Completed Successfully!</h3>
              <p className="text-sm leading-relaxed text-slate-500">
                Data updated till <span className="font-semibold text-slate-700">{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>.
                <br/>
                <span className="mt-2 inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-[#136bce] ring-1 ring-inset ring-blue-700/10">42 records processed</span>
              </p>
            </div>
            <div className="absolute bottom-0 left-0 h-1 w-full bg-slate-50">
              <div className="h-full w-full bg-emerald-500 opacity-20" style={{ width: '100%' }}></div>
            </div>
            <button
              className="mt-6 text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors"
              onClick={() => setIsSyncSuccessModalOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Upload Purchase Order Modal Overlay */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-[4px] px-4 sm:px-0" id="upload-file-modal">
          <div
            className="absolute inset-0 bg-slate-900/60 transition-opacity"
            onClick={() => setIsUploadModalOpen(false)}
          ></div>
          <div className="relative w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-8 shadow-2xl transition-all border border-slate-100">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Upload Purchase Order</h3>
                <p className="mt-1 text-sm text-slate-500">Import your PO data from a file.</p>
              </div>
              <button
                className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                onClick={() => setIsUploadModalOpen(false)}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="mb-6 rounded-lg bg-blue-50 border border-blue-100 p-4 flex items-start gap-3">
              <div className="text-[#136bce] mt-0.5">
                <span className="material-symbols-outlined text-[20px]">info</span>
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-700 font-medium">Prepare your file correctly</p>
                <p className="text-xs text-slate-500 mt-1">Ensure your file matches one of our templates to avoid errors during import: the standard one (one row per SKU) or the size-wise one (one column per size). Supported formats: Excel, CSV.</p>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <button onClick={downloadSampleCSV} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#136bce] hover:text-blue-700 hover:underline">
                    <span className="material-symbols-outlined text-[16px]">download</span>
                    Download Sample File
                  </button>
                  <button onClick={downloadSizeWiseSample} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#136bce] hover:text-blue-700 hover:underline">
                    <span className="material-symbols-outlined text-[16px]">download</span>
                    Download Size-wise Sample
                  </button>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className={`group flex flex-col items-center justify-center w-full h-48 rounded-xl border-2 border-dashed cursor-pointer transition-all ${uploadFile ? 'border-[#136bce] bg-blue-50/50' : 'border-slate-300 bg-slate-50 hover:bg-blue-50 hover:border-[#136bce]'}`}>
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  {uploadFile ? (
                    <>
                      <div className="mb-3 h-12 w-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                        <span className="material-symbols-outlined text-[24px]">description</span>
                      </div>
                      <p className="mb-1 text-sm text-slate-900 font-semibold">{uploadFile.name}</p>
                      <p className="text-xs text-slate-500">{(uploadFile.size / 1024).toFixed(1)} KB</p>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); setUploadFile(null); setUploadError(''); }}
                        className="mt-2 text-xs font-semibold text-red-500 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="mb-3 h-12 w-12 rounded-full bg-blue-100 text-[#136bce] flex items-center justify-center group-hover:scale-110 transition-transform">
                        <span className="material-symbols-outlined text-[24px]">cloud_upload</span>
                      </div>
                      <p className="mb-2 text-sm text-slate-700 font-medium group-hover:text-[#136bce]">Click to upload or drag and drop</p>
                      <p className="text-xs text-slate-500">Excel, CSV files only (.xlsx, .xls, .csv)</p>
                    </>
                  )}
                </div>
                <input
                  className="hidden"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => { setUploadFile(e.target.files?.[0] || null); setUploadError(''); e.target.value = ''; }}
                />
              </label>
            </div>

            {uploadError && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 flex items-start gap-2">
                <span className="material-symbols-outlined text-red-500 text-[18px] mt-0.5">error</span>
                <p className="text-xs text-red-700 font-medium">{uploadError}</p>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100 mt-2">
              <button
                className="px-5 py-2.5 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition-colors"
                onClick={() => {
                  setIsUploadModalOpen(false);
                  setIsNewPOModalOpen(true);
                  setUploadFile(null);
                  setUploadError('');
                }}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                onClick={handleUploadFile}
                disabled={uploading || !uploadFile}
                className="px-5 py-2.5 rounded-lg bg-[#136bce] text-sm font-medium text-white shadow-lg shadow-blue-500/20 hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {uploading && <span className="material-symbols-outlined animate-spin text-[16px]">progress_activity</span>}
                {uploading ? 'Processing...' : 'Upload File'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create New PO Modal Overlay */}
      {isNewPOModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-[4px] p-4">
          <div className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-8 shadow-2xl transition-all">
            <div className="mb-8 text-center">
              <h3 className="text-xl font-bold text-slate-900">Add New Purchase Order</h3>
              <p className="mt-2 text-sm text-slate-500">Choose how you would like to create the new PO.</p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <button
                onClick={handleOpenUpload}
                className="group flex flex-col items-center justify-center rounded-xl border-2 border-slate-100 bg-slate-50 p-6 transition-all hover:border-[#136bce] hover:bg-blue-50 hover:shadow-md"
              >
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-[#136bce] group-hover:bg-[#136bce] group-hover:text-white transition-colors">
                  <span className="material-symbols-outlined text-[24px]">upload_file</span>
                </div>
                <span className="text-sm font-semibold text-slate-700 group-hover:text-[#136bce]">Upload File</span>
                <span className="mt-1 text-xs text-slate-400 group-hover:text-blue-400">CSV, Excel</span>
              </button>

              <button
                onClick={handleManualEntry}
                className="group flex flex-col items-center justify-center rounded-xl border-2 border-slate-100 bg-slate-50 p-6 transition-all hover:border-[#136bce] hover:bg-blue-50 hover:shadow-md"
              >
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-[#136bce] group-hover:bg-[#136bce] group-hover:text-white transition-colors">
                  <span className="material-symbols-outlined text-[24px]">edit_document</span>
                </div>
                <span className="text-sm font-semibold text-slate-700 group-hover:text-[#136bce]">Enter Manually</span>
                <span className="mt-1 text-xs text-slate-400 group-hover:text-blue-400">Fill form details</span>
              </button>

              <button
                onClick={handleSyncFromERP}
                className="group flex flex-col items-center justify-center rounded-xl border-2 border-slate-100 bg-slate-50 p-6 transition-all hover:border-[#136bce] hover:bg-blue-50 hover:shadow-md"
              >
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-[#136bce] group-hover:bg-[#136bce] group-hover:text-white transition-colors">
                  <span className="material-symbols-outlined text-[24px]">sync</span>
                </div>
                <span className="text-sm font-semibold text-slate-700 group-hover:text-[#136bce]">Sync from ERP</span>
                <span className="mt-1 text-xs text-slate-400 group-hover:text-blue-400">Connect ERP System</span>
              </button>
            </div>

            <div className="mt-8 flex justify-center">
              <button
                className="text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors"
                onClick={() => setIsNewPOModalOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign PO Modal Overlay */}
      {isAssignModalOpen && (
        <div aria-labelledby="modal-title" aria-modal="true" className="fixed inset-0 z-[60] overflow-y-auto" role="dialog">
          <div aria-hidden="true" className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"></div>
          <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-visible rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-xl border border-slate-100">
              <button
                className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 transition-colors z-10 p-1 rounded-full hover:bg-slate-100"
                type="button"
                onClick={() => setIsAssignModalOpen(false)}
              >
                <span className="material-symbols-outlined text-[24px]">close</span>
              </button>
              <div className="p-8">
                <div className="mb-8 border-b border-slate-100 pb-6">
                  <div className="flex items-center gap-5">
                    <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-[#136bce] ring-1 ring-blue-100 shadow-sm">
                      <span className="material-symbols-outlined text-[28px]">assignment_ind</span>
                    </div>
                    <div className="pt-1">
                      <h3 className="text-xl font-bold leading-6 text-slate-900 tracking-tight" id="modal-title">Assign Purchase Order</h3>
                      <p className="text-sm text-slate-500 mt-1.5">Assign an inspector and set a due date for <span className="font-semibold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">{selectedPOs.map(id => purchaseOrders.find(p => p.id === id)?.po_number).filter(Boolean).join(', ')}</span>.</p>
                    </div>
                  </div>
                </div>
                <form className="space-y-6">
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="inspector">
                        QA Inspector <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                          <span className="material-symbols-outlined text-slate-400 text-[20px]">person_search</span>
                        </div>
                        <select className="appearance-none block w-full rounded-xl border-slate-200 bg-white py-3 pl-11 pr-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-[#136bce] focus:border-[#136bce] sm:text-sm sm:leading-6 transition-all cursor-pointer hover:bg-slate-50/50" id="inspector" name="inspector">
                          <option disabled selected value="">Choose an inspector...</option>
                          {users.map(u => (
                            <option key={u.id} value={u.id}>{u.name}</option>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <span className="material-symbols-outlined text-slate-400 text-[24px]">expand_more</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="inspection-type">
                        Inspection Module <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                          <span className="material-symbols-outlined text-slate-400 text-[20px]">fact_check</span>
                        </div>
                        <select className="appearance-none block w-full rounded-xl border-slate-200 bg-white py-3 pl-11 pr-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-[#136bce] focus:border-[#136bce] sm:text-sm sm:leading-6 transition-all cursor-pointer hover:bg-slate-50/50" id="inspection-type" name="inspection-type" defaultValue="final">
                          <option disabled value="">Select inspection type...</option>
                          <option value="pilot">Pilot Inspection</option>
                          <option value="fabric">Fabric Inspection</option>
                          <option value="line">Line Inspection</option>
                          <option value="final">Final Inspection</option>
                          <option value="container">Container Inspection</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <span className="material-symbols-outlined text-slate-400 text-[24px]">expand_more</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="inspection-stage">
                        Inspection Type
                      </label>
                      <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                          <span className="material-symbols-outlined text-slate-400 text-[20px]">rule</span>
                        </div>
                        <select className="appearance-none block w-full rounded-xl border-slate-200 bg-white py-3 pl-11 pr-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-[#136bce] focus:border-[#136bce] sm:text-sm sm:leading-6 transition-all cursor-pointer hover:bg-slate-50/50" id="inspection-stage" name="inspection-stage" defaultValue="">
                          <option disabled value="">Select inspection type...</option>
                          <option value="Intro">Intro</option>
                          <option value="Dupro">Dupro</option>
                          <option value="Final">Final</option>
                          <option value="Re-Final">Re-Final</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <span className="material-symbols-outlined text-slate-400 text-[24px]">expand_more</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="due-date">
                        Due Date <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <DatePicker
                          value={dueDate}
                          onChange={(date) => setDueDate(date)}
                          placeholder="Select due date"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="instructions">
                        Special Instructions
                      </label>
                      <div className="relative">
                        <div className="pointer-events-none absolute top-3 left-0 flex items-center pl-3.5">
                          <span className="material-symbols-outlined text-slate-400 text-[20px]">notes</span>
                        </div>
                        <textarea className="block w-full rounded-xl border-slate-200 bg-white py-3 pl-11 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-[#136bce] focus:border-[#136bce] sm:text-sm sm:leading-6 transition-all resize-none hover:bg-slate-50/50" id="instructions" name="instructions" placeholder="Enter specific inspection guidelines or notes..." rows={3}></textarea>
                      </div>
                    </div>
                  </div>
                </form>
                <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-slate-100">
                  <button
                    className="inline-flex w-full justify-center items-center rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 hover:text-slate-900 sm:w-auto transition-all"
                    type="button"
                    onClick={() => setIsAssignModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button className="inline-flex w-full justify-center items-center rounded-xl bg-[#136bce] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 hover:bg-blue-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 sm:w-auto transition-all group" type="button">
                    <span className="material-symbols-outlined text-[20px] mr-2">check_circle</span>
                    Assign
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Purchase Orders</h1>
          <p className="text-sm text-slate-500">Manage, track and assign purchase orders for inspection.</p>
        </div>
        <div className="flex flex-wrap gap-3">

          <button
            onClick={() => navigate('/purchase-orders/calendar')}
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">calendar_month</span>
            View Calendar
          </button>
          <button
            onClick={() => setIsNewPOModalOpen(true)}
            className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-lg shadow-blue-500/20 hover:bg-blue-700 transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">add</span>
            Create New Purchase Order
          </button>
        </div>
      </div>

      <div className="flex flex-col rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 p-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <SearchHistoryInput
              scope="purchase_orders"
              className="w-full max-w-md"
              placeholder="Search PO Number, Customer..."
              value={searchQuery}
              onChange={setSearchQuery}
            />

            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-700">Ship Date:</span>
              <DateRangePicker
                range={{ from: dateFrom, to: dateTo }}
                onChange={(range) => {
                  setDateFrom(range.from);
                  setDateTo(range.to);
                }}
                onClear={() => {
                  setDateFrom('');
                  setDateTo('');
                }}
              />
              <div className="relative" ref={columnsMenuRef}>
                <button
                  onClick={() => setIsColumnsMenuOpen(o => !o)}
                  className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  <span className="material-symbols-outlined text-[18px]">view_column</span>
                  Columns
                  <span className="material-symbols-outlined text-[18px]">expand_more</span>
                </button>
                {isColumnsMenuOpen && (
                  <div className="absolute right-0 z-30 mt-2 w-56 rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
                    <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Show Columns</p>
                    {COLUMN_DEFS.map(col => {
                      const checked = !!visibleColumns[col.key];
                      const isLastVisible = checked && visibleColumnCount === 1;
                      return (
                        <label
                          key={col.key}
                          className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50 ${isLastVisible ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-[#136bce] focus:ring-[#136bce]"
                            checked={checked}
                            disabled={isLastVisible}
                            onChange={() => toggleColumn(col.key)}
                          />
                          {col.label}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <button onClick={handleExportExcel} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
                <span className="material-symbols-outlined text-[18px]">download</span>
                Export
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                {visibleColumns.po_number && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">PO Number</th>}
                {visibleColumns.work_order && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Work Order</th>}
                {visibleColumns.customer && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Customer</th>}
                {visibleColumns.assigned_to && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Assigned To</th>}
                {visibleColumns.assigned_by && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Assigned By</th>}
                {visibleColumns.ship_date && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Ship Date</th>}
                {visibleColumns.inspections && <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Inspections</th>}
                {visibleColumns.actions && <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={visibleColumnCount} className="px-6 py-12 text-center text-sm text-slate-500">
                    <div className="flex items-center justify-center gap-2">
                      <span className="material-symbols-outlined animate-spin text-[20px]">progress_activity</span>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : purchaseOrders.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumnCount} className="px-6 py-12 text-center text-sm text-slate-500">
                    No purchase orders found.
                  </td>
                </tr>
              ) : purchaseOrders.map((po) => {
                const inspectionStatuses = po.inspection_statuses || {};
                const isToday = new Date(po.created_at).toDateString() === new Date().toDateString();
                return (
                  <tr
                    key={po.id}
                    className="group hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/purchase-orders/${po.id}`)}
                  >
                    {visibleColumns.po_number && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-[#136bce] group-hover:underline">{po.po_number}</div>
                      {isToday && <div className="text-xs text-slate-500">Added today</div>}
                    </td>
                    )}
                    {visibleColumns.work_order && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-700">{po.work_order || '-'}</div>
                    </td>
                    )}
                    {visibleColumns.customer && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-slate-900">{po.customer_name}</div>
                      <div className="text-xs text-slate-500">{po.customer_location}</div>
                    </td>
                    )}
                    {visibleColumns.assigned_to && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <PeopleHoverCard title="Assigned To" people={getAssignedToPeople(po)} emptyLabel="Unassigned" showTicks />
                      </div>
                    </td>
                    )}
                    {visibleColumns.assigned_by && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <PeopleHoverCard title="Assigned By" people={getAssignedByPeople(po)} emptyLabel="-" />
                      </div>
                    </td>
                    )}
                    {visibleColumns.ship_date && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{formatDate(po.ship_date)}</td>
                    )}
                    {visibleColumns.inspections && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {['pilot', 'fabric', 'line', 'final', 'container'].map((key) => {
                          const status = inspectionStatuses[key] || 'Pending';
                          const labels: Record<string, string> = {
                            pilot: 'Pi',
                            fabric: 'Fa',
                            line: 'Li',
                            final: 'Fi',
                            container: 'Co'
                          };
                          const label = labels[key];
                          return (
                            <div key={key} className="group/tooltip relative">
                              <div className={`flex h-6 w-6 items-center justify-center rounded text-[10px] font-bold border ${
                                status === 'Completed' || status === 'Passed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                status === 'In Progress' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                status === 'Scheduled' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                status === 'Failed' ? 'bg-red-50 text-red-700 border-red-200' :
                                'bg-slate-50 text-slate-400 border-slate-200'
                              }`}>
                                {label}
                              </div>
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/tooltip:block z-20">
                                <div className="bg-slate-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap shadow-xl font-medium">
                                  <span className="capitalize">{key}</span>: {status}
                                </div>
                                <div className="w-1.5 h-1.5 bg-slate-900 rotate-45 absolute -bottom-0.5 left-1/2 -translate-x-1/2" />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    )}
                    {visibleColumns.actions && (
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(po);
                        }}
                        className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete"
                      >
                        <span className="material-symbols-outlined text-[20px]">delete</span>
                      </button>
                    </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 sm:px-6">
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-700">
                Showing <span className="font-medium">{purchaseOrders.length > 0 ? ((currentPage - 1) * 20) + 1 : 0}</span> to <span className="font-medium">{Math.min(currentPage * 20, totalCount)}</span> of <span className="font-medium">{totalCount}</span> results
              </p>
            </div>
            {totalPages > 1 && (
              <div>
                <nav aria-label="Pagination" className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                  <button
                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    <span className="material-symbols-outlined text-[20px]">chevron_left</span>
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                        page === currentPage
                          ? 'z-10 bg-[#136bce] text-white focus:z-20'
                          : 'text-slate-900 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus:z-20'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  {totalPages > 5 && (
                    <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-inset ring-slate-300">...</span>
                  )}
                  <button
                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    <span className="material-symbols-outlined text-[20px]">chevron_right</span>
                  </button>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrders;
